import { Response, apiAbortController } from '@ui/src';
import api from '../../../api/api';
import { Dictionary, DictionaryItem, DictionaryItemFormValues } from '../types';

export const dictionariesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getDictionaries: build.query<Response<Dictionary>, string>({
      query: (query) => ({
        url: `/v1/dictionaries${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getDictionary: build.query<{ data: Dictionary }, string>({
      query: (id) => ({
        url: `/v1/dictionaries/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getDictionaryItems: build.query<
      Response<DictionaryItem>,
      { id: string; query: string }
    >({
      query: ({ id, query }) => ({
        url: `/v1/dictionaries/${id}/items${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getDictionaryItem: build.query<
      { data: DictionaryItem },
      { id: string; itemId: string }
    >({
      query: ({ id, itemId }) => ({
        url: `/v1/dictionaries/${id}/items/${itemId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createDictionaryItem: build.mutation<
      { data: DictionaryItem },
      { id: string; body: DictionaryItemFormValues }
    >({
      query: ({ id, body }) => ({
        url: `/v1/dictionaries/${id}/items`,
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateDictionaryItem: build.mutation<
      { data: DictionaryItem },
      { id: string; itemId: string; body: DictionaryItemFormValues }
    >({
      query: ({ id, body, itemId }) => ({
        url: `/v1/dictionaries/${id}/items/${itemId}`,
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteDictionaryItem: build.mutation<
      { data: DictionaryItem },
      { id: string; itemId: string }
    >({
      query: ({ id, itemId }) => ({
        url: `/v1/dictionaries/${id}/items/${itemId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchDeleteDictionaryItems: build.mutation<
      { data: DictionaryItem },
      {
        id: string;
        body: {
          ids: string[];
        };
      }
    >({
      query: ({ id, body }) => ({
        url: `/v1/dictionaries/${id}/items/batch-delete`,
        method: 'DELETE',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetDictionariesQuery,
  useLazyGetDictionaryItemsQuery,
  useLazyGetDictionaryQuery,
  useLazyGetDictionaryItemQuery,
  useCreateDictionaryItemMutation,
  useUpdateDictionaryItemMutation,
  useDeleteDictionaryItemMutation,
  useBatchDeleteDictionaryItemsMutation,
} = dictionariesApi;

export const {
  endpoints: {
    getDictionaries,
    getDictionaryItems,
    getDictionary,
    getDictionaryItem,
    createDictionaryItem,
    updateDictionaryItem,
    deleteDictionaryItem,
    batchDeleteDictionaryItems,
  },
} = dictionariesApi;
